/**
 * MapPage - Map view page component
 * Uses AppShell for consistent layout
 */

import React, { useMemo, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { LatLngTuple } from 'leaflet';
import { Search, GitCompare, HelpCircle, X, MapPin, ShoppingBag, Waves, Navigation } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import AppShell from '../../layout/AppShell';
import { HeaderConfig } from '../../layout/AppHeader';
import './MapPage.css';

interface MapPageProps {
  mapCenter: LatLngTuple;
  mapZoom: number;
  markers: React.ReactNode[];
  onLocationSelect?: (location: any) => void;
  onSearchClick?: () => void;
  onCompareClick?: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  onMyVibeClick?: () => void;
  hideNavbar?: boolean; // Hide navbar for native app
  /** App-wide user location (from App state) */
  userLocation?: LatLngTuple | null;
  isLocating?: boolean;
  onRequestLocation?: () => Promise<LatLngTuple>;
  /** Center map on current location (uses App location) */
  centerOnCurrentLocation?: () => Promise<void>;
}

// MapController component
const MapController: React.FC<{
  onLocationSelect: (location: any) => void;
  mapRef: React.MutableRefObject<any>;
}> = ({ onLocationSelect, mapRef }) => {
  const map = useMap();

  React.useEffect(() => {
    if (map && mapRef) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  return null;
};

const MapPage: React.FC<MapPageProps> = ({
  mapCenter,
  mapZoom,
  markers,
  onLocationSelect,
  onSearchClick,
  onCompareClick,
  activeTab,
  onTabChange,
  onMyVibeClick,
  hideNavbar = false,
  userLocation: _userLocation,
  isLocating = false,
  onRequestLocation,
  centerOnCurrentLocation,
}) => {
  const mapRef = React.useRef<any>(null);
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Use App's centerOnCurrentLocation; fallback no-op if not provided
  const handleCenterOnLocation = useCallback(async () => {
    if (centerOnCurrentLocation) {
      await centerOnCurrentLocation();
    }
  }, [centerOnCurrentLocation]);

  // Create header configuration - matching Home2 alignment
  const headerConfig: HeaderConfig = useMemo(() => ({
    left: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <h1 className="home2-title">VibeCheck</h1>
        {onCompareClick && (
          <button
            onClick={onCompareClick}
            className="map-compare-button"
            title="Compare locations"
            style={{
              height: '36px',
              padding: '0 0.875rem',
              fontSize: '0.875rem',
              marginLeft: '0.5rem',
            }}
          >
            <GitCompare size={16} />
            <span>Compare</span>
          </button>
        )}
      </div>
    ),
    right: onSearchClick ? (
      <button
        onClick={onSearchClick}
        className="home2-search-button"
        title="Search"
        aria-label="Search"
      >
        <Search size={18} />
      </button>
    ) : undefined,
    backgroundColor: '#7c3aed',
    variant: 'default'
  }), [onSearchClick, onCompareClick]);

  return (
    <AppShell
      headerConfig={headerConfig}
      showFooter={!hideNavbar}
      activeTab={activeTab}
      onTabChange={onTabChange}
      onMyVibeClick={onMyVibeClick}
    >
      <div className="map-page">
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          zoomControl={false}
          className="map-page-container"
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            subdomains="abcd"
            maxZoom={20}
          />
          <MarkerClusterGroup
            chunkedLoading={true}
            chunkInterval={150}
            chunkDelay={100}
            removeOutsideVisibleBounds={true}
            disableClusteringAtZoom={16}
            maxClusterRadius={60}
            spiderfyOnMaxZoom={true}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            animate={false}
            animateAddingMarkers={false}
          >
            {markers}
          </MarkerClusterGroup>
          {onLocationSelect && (
            <MapController onLocationSelect={onLocationSelect} mapRef={mapRef} />
          )}

          {/* Current Location Button and Help Button */}
          <div className="current-location-button-container">
            <button
              onClick={handleCenterOnLocation}
              disabled={isLocating}
              className="current-location-button"
              title="Center on my location"
            >
              {isLocating ? (
                <div className="location-spinner"></div>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                  <circle cx="12" cy="9" r="2.5" />
                </svg>
              )}
            </button>
            <button
              onClick={() => setShowHelpModal(true)}
              className="current-location-button help-button"
              title="How to use Map View"
              style={{ marginTop: '8px' }}
            >
              <HelpCircle size={20} />
            </button>
          </div>
        </MapContainer>
      </div>

      {/* Help Modal - Portal to document.body */}
      {createPortal(
        <AnimatePresence>
          {showHelpModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="coffee-filter-modal-overlay"
              onClick={() => setShowHelpModal(false)}
            >
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="coffee-filter-modal"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="coffee-filter-modal-header">
                  <h2>How to Use Map View</h2>
                  <button onClick={() => setShowHelpModal(false)} className="coffee-filter-clear-button">
                    <X size={20} />
                  </button>
                </div>
                <div className="coffee-filter-modal-content" style={{ padding: '24px 20px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {/* Locations Supported Section */}
                    <div
                      style={{
                        background: 'rgba(124, 58, 237, 0.05)',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid rgba(124, 58, 237, 0.1)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            background: 'rgba(124, 58, 237, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <MapPin size={20} color="#7c3aed" />
                        </div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                          Locations Supported
                        </h3>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {[
                          {
                            icon: ShoppingBag,
                            title: 'Shopping Centres',
                            desc: 'Browse shopping centres and retail locations. View details, opening hours, and amenities. For shopping centres, see individual shops within each centre. For supported Costco locations, view the store plan layout.',
                          },
                          {
                            icon: Waves,
                            title: 'Beaches',
                            desc: 'Discover beaches and coastal locations. See facilities, parking information, and nearby amenities.',
                          },
                          {
                            icon: MapPin,
                            title: 'Other Locations',
                            desc: 'Explore various points of interest including parks, landmarks, and popular destinations.',
                          },
                        ].map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '12px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                            }}
                          >
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '8px',
                                background: 'rgba(124, 58, 237, 0.1)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                              }}
                            >
                              <item.icon size={16} color="#7c3aed" />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: '0.9375rem',
                                  fontWeight: 600,
                                  color: '#111827',
                                  marginBottom: '4px',
                                }}
                              >
                                {item.title}
                              </div>
                              <div
                                style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}
                              >
                                {item.desc}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Information Available Section */}
                    <div
                      style={{
                        background: 'rgba(59, 130, 246, 0.05)',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid rgba(59, 130, 246, 0.1)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            background: 'rgba(59, 130, 246, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <MapPin size={20} color="#3b82f6" />
                        </div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                          Information Available
                        </h3>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Location Details</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            Tap any marker on the map to view detailed information including name, address, opening hours,
                            contact details, and distance from your current location.
                          </div>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Distance & Navigation</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            See real-time distance calculations from your location. Use the navigation button to get
                            directions in your preferred map app.
                          </div>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Compare Locations</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            Use the Compare button in the header to select and compare multiple locations side-by-side.
                          </div>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Crowd Level & Vibe Planner</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            View real-time crowd levels for locations. Use the Vibe Planner to find the best time to visit
                            based on historical crowd data and avoid peak times.
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tips Section */}
                    <div
                      style={{
                        background: 'rgba(16, 185, 129, 0.05)',
                        borderRadius: '16px',
                        padding: '20px',
                        border: '1px solid rgba(16, 185, 129, 0.1)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <div
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '12px',
                            background: 'rgba(16, 185, 129, 0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Navigation size={20} color="#10b981" />
                        </div>
                        <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                          Tips for Using Map View
                        </h3>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Pinch to Zoom</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            Use pinch gestures on mobile or the scroll wheel on desktop to zoom in and out of the map.
                            Clusters will automatically expand as you zoom in.
                          </div>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Current Location</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            Tap the location icon button to center the map on your current position. Make sure location
                            permissions are enabled in your browser settings.
                          </div>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Marker Clusters</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            When zoomed out, nearby markers are grouped into clusters. Tap a cluster to zoom in and see
                            individual locations. The number on the cluster shows how many locations are grouped together.
                          </div>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          <div style={{ marginBottom: '8px', fontWeight: 600 }}>Search Function</div>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                            Use the search icon in the header to quickly find specific locations by name or browse by
                            category.
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </AppShell>
  );
};

export default MapPage;
